import { dispatch, useReframe } from 'nike-re-framejs';
import React, { FC, useContext, useEffect } from 'react';
import TurboLiveContext from '../../context/TurboLiveContext';
import clsx from 'clsx';
import { CashinButtonLive } from './CashinButtonLive';
import { useBetCashinCollapsed, useBetsCashinOffers } from './hooks/useCashinOffers';
import { cashinBetsToggle, cashinCollapsedInit, cashinSportEventToggle } from './handlers';
import ToggleBtn from '../ToggleBtn';
import { expandedMatchTickets$$ } from '../tickets/subs';
import { matchActiveTicketsToggle } from '../tickets/handlers';
import i18n from '../../../../common/i18n';
import { isMobileChannel } from '../../../../common/util';
import { cashinMatchCollapsed$$, cashinOfferDataForMatch$$ } from './subs';

export function useCashinOfferSubscription() {
    const { startCashinOfferSubscription, stopCashinOfferSubscription } = useContext(TurboLiveContext);
    useEffect(() => {
        startCashinOfferSubscription();
        dispatch(cashinCollapsedInit);
        return () => {
            stopCashinOfferSubscription();
        };
    }, [startCashinOfferSubscription, stopCashinOfferSubscription]);
}

const CashinBetOffersList: FC<{ betIds: any }> = ({ betIds }) => {
    const betCashinOffers = useBetsCashinOffers(betIds);
    useCashinOfferSubscription();

    return <CashinOffersList cashinOffers={betCashinOffers} isDetail={true} />;
};

export const CashinBetsListCollapsible: FC<{ betIds: any }> = ({ betIds }) => {
    const collapsed = useBetCashinCollapsed(betIds);
    return collapsed ? null : <CashinBetOffersList betIds={betIds} />;
};

const CashinMatchOffersList: FC<{ matchId: any }> = ({ matchId }) => {
    const { derefSub } = useReframe();
    const eventCashinOffers = derefSub(cashinOfferDataForMatch$$(matchId));
    useCashinOfferSubscription();

    return <CashinOffersList cashinOffers={eventCashinOffers} isDetail={false} />;
};

const CashinOffersList: FC<{ cashinOffers: any; isDetail: boolean }> = ({ cashinOffers, isDetail }) => {
    if (!cashinOffers || !cashinOffers.size) {
        return null;
    }
    return (
        <div
            className={clsx(
                'flex items-center justify-between bg-white-ng',
                { 'mb-8': isMobileChannel() && !isDetail },
                isMobileChannel() || isDetail ? 'br-default-light' : 'br-t-default-light'
            )}
        >
            <span className="pl-10 fs-13 text-extra-bold c-black-100">{i18n('nui.cashin.payOff')}</span>

            <ul className=" flex flex-wrap justify-end mr-5 mb-5">
                {cashinOffers.map(cashin => {
                    return (
                        <li className="mr-5 mt-5" key={cashin.get('ticketId')}>
                            <CashinButtonLive ticketId={cashin.get('ticketId')} />
                        </li>
                    );
                })}
            </ul>
        </div>
    );
};

export const CashinMatchToggle: FC<{ matchId: string; usePlaceholder?: boolean }> = ({ matchId, usePlaceholder }) => {
    const derefSub = useReframe();
    const cashinOffers = derefSub(cashinOfferDataForMatch$$(matchId));
    const activeTickets = derefSub(expandedMatchTickets$$(matchId));
    const collapsed = derefSub(cashinMatchCollapsed$$(matchId));
    if (!cashinOffers?.size) {
        return usePlaceholder ? <span className="px-2 py-8" /> : null;
    }
    const onClick = e => {
        if (!isMobileChannel()) {
            if (activeTickets && collapsed) {
                dispatch(matchActiveTicketsToggle(matchId));
            }
            dispatch(cashinSportEventToggle(matchId));
        }
        e.preventDefault();
        e.stopPropagation();
    };
    return (
        <ToggleBtn
            imgId="live-cashin"
            titleKey="nui.turbo.live.icon.cashin"
            onClick={onClick}
            active={!collapsed}
            badge={cashinOffers.size}
        />
    );
};

export const CashinMatchCollapsible: FC<{ matchId: string }> = ({ matchId }) => {
    const derefSub = useReframe();
    const collapsed = derefSub(cashinMatchCollapsed$$(matchId));
    return collapsed ? null : <CashinMatchOffersList matchId={matchId} />;
};

export const CashinDetailMarketToggle: FC<{ betIds: any }> = ({ betIds }) => {
    const betCashinOffers = useBetsCashinOffers(betIds);
    const collapsed = useBetCashinCollapsed(betIds);
    if (!betIds?.size || !betCashinOffers?.size) {
        return null;
    }
    const onClick = e => {
        if (!isMobileChannel()) {
            dispatch(cashinBetsToggle(betIds));
        }
        e.preventDefault();
        e.stopPropagation();
    };
    return (
        <ToggleBtn
            imgId="live-cashin"
            titleKey="nui.turbo.live.icon.cashin"
            data-atid="cashin-detail-market-toggle-button"
            onClick={onClick}
            active={!collapsed}
            badge={betCashinOffers.size}
            otherClass="py-5"
        />
    );
};
